import React, { useEffect, useState } from 'react'
import { BulkActionDialog, BulkActionDialogActions } from '../../components/ui/BulkActionDialog'
import { Callout } from '../../components/ui/callout'
import { customFieldApiError, customFieldsApi } from './api'
import type { CustomFieldDefinition } from './types'

export interface CustomFieldDeleteModalProps {
    isOpen: boolean
    definition: CustomFieldDefinition | null
    profileId: string
    onClose: () => void
    onDeleted: () => void
}

export const CustomFieldDeleteModal = ({
    isOpen,
    definition,
    profileId,
    onClose,
    onDeleted,
}: CustomFieldDeleteModalProps) => {
    const [error, setError] = useState<string | null>(null)
    const [dependants, setDependants] = useState<Array<{ recipe_id: string; name: string }> | null>(
        null,
    )
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        setError(null)
        setDependants(null)
    }, [isOpen])

    if (!definition) return null

    const remove = async (disableDependants: boolean) => {
        setSaving(true)
        setError(null)
        try {
            await customFieldsApi.deleteDefinition(profileId, definition, disableDependants)
            onDeleted()
            onClose()
        } catch (requestError: any) {
            const payload = requestError?.response?.data?.error
            if (payload?.code === 'definition_has_active_dependants') {
                setDependants(payload.details?.dependants ?? [])
            } else {
                setError(customFieldApiError(requestError))
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={onClose}
            title={`Delete ${definition.key}`}
            description={
                dependants
                    ? undefined
                    : `This permanently deletes "${definition.name}" and its values. This cannot be undone.`
            }
            width="compact"
            dismissible={!saving}
            footer={
                <BulkActionDialogActions
                    primaryLabel={dependants ? 'Disable segments and delete' : 'Delete'}
                    loadingLabel="Deleting…"
                    loading={saving}
                    destructive
                    onPrimary={() => remove(!!dependants)}
                    onCancel={onClose}
                />
            }
        >
            {error && <Callout intent="danger" title={error} />}
            {dependants && (
                <Callout intent="warning" title="Active segments use this field">
                    <ul className="my-2 list-disc pl-5">
                        {dependants.map((dependant) => (
                            <li key={dependant.recipe_id}>{dependant.name}</li>
                        ))}
                    </ul>
                </Callout>
            )}
        </BulkActionDialog>
    )
}
